/**
 * The ONE place that states how The Production Harness can be obtained.
 *
 * WHY A COMPONENT AND NOT COPY IN EACH PAGE
 * -----------------------------------------
 * Access terms were previously written independently on three surfaces — the
 * home product card, the course home, and the module paywall. They disagreed:
 * the card implied the course was excluded from All-Access while the router
 * granted it to every subscriber (D-012), and the paywall — cloned verbatim
 * from Agentic Ops — offered "Upgrade to All-Access" as the only door, leaving
 * a $149 buyer with no purchase option at all. Nothing could detect the
 * disagreement, because it was prose in three JSON files.
 *
 * So the terms are stated once, here. This mirrors what the backend already
 * does: every gating path in claude_harness.py funnels through _is_locked,
 * after a second code path once bypassed the freshness agent's authority gate.
 *
 * D-012: there are exactly TWO doors, and both must always be visible.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import { createCourseCheckoutSession } from "../../api/client";
import { useAuth } from "../../hooks/useAuth";
import { toUserSafeErrorMessage } from "../../utils/errorMessage";

export default function HarnessAccessCTA({ dense = false }: { dense?: boolean }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-core");
  const { isAuthenticated } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function buyOnce() {
    // Checkout requires a user (the webhook grants the entitlement to a user id),
    // so an anonymous buyer must sign in FIRST — otherwise the click returns a
    // bare 401 on the exact screen where they decided to pay. Same pattern and
    // same `from` state as PricingPage's tier checkout.
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/claude-harness" } });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { checkout_url } = await createCourseCheckoutSession();
      window.location.href = checkout_url;
    } catch (e: unknown) {
      // Surface the server's reason: it distinguishes "you already own this"
      // (400) from "no price configured" (503), and a generic message would
      // send an owner to support for nothing.
      setError(toUserSafeErrorMessage(e, t("HarnessAccess.checkoutError")));
      setBusy(false);
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: dense ? 2.5 : 3.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <Typography variant={dense ? "h6" : "h5"} fontWeight={700} sx={{ mb: 1 }}>
        {t("HarnessAccess.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 620 }}>
        {t("HarnessAccess.body")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
        <Button variant="contained" size="large" onClick={buyOnce} disabled={busy}>
          {t("HarnessAccess.buyOnce")}
        </Button>
        <Button variant="outlined" size="large" onClick={() => navigate("/pricing")}>
          {t("HarnessAccess.getAllAccess")}
        </Button>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t("HarnessAccess.footnote")}
        </Typography>
      </Box>
    </Paper>
  );
}
